// URS-F186: Year-wide weekly schedule view for Agenda. Aggregates
// schedule_slots across all studies and groups by ISO week. Each cell shows
// chips with border-only style coloured per study + visit_code + count.
// Click a chip -> popover with slot details and a 'Edit in study' shortcut.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarBlank, CaretRight } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { MONTHS_ES, MONTHS_EN } from './agendaConstants';
import { isoWeekOfDate, isoWeekStart, weeksInYear } from '../../utils/isoWeek';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ScheduleYearView({ lang }) {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const today = new Date();
  const todayIso = isoWeekOfDate(today);
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const currentRowRef = useRef(null);
  const scrolledRef = useRef(false);

  const T = lang === 'es' ? {
    title: 'Programacion anual',
    year: 'Ano',
    week: 'Semana',
    schedule: 'Programacion',
    thisWeek: 'ESTA SEMANA',
    total: 'huecos',
    empty: 'Sin programacion esta semana',
    editInStudy: 'Editar en estudio',
    loading: 'Cargando...',
    error: 'Error al cargar programacion',
  } : {
    title: 'Yearly schedule',
    year: 'Year',
    week: 'Week',
    schedule: 'Schedule',
    thisWeek: 'THIS WEEK',
    total: 'slots',
    empty: 'No schedule this week',
    editInStudy: 'Edit in study',
    loading: 'Loading...',
    error: 'Error loading schedule',
  };

  const MONTHS = lang === 'es' ? MONTHS_ES : MONTHS_EN;

  useEffect(() => {
    setLoading(true);
    scrolledRef.current = false; // reset when year changes
    axios.get(`${API_URL}/api/agenda/schedule-by-week`, {
      params: { year },
      withCredentials: true,
    })
      .then(({ data }) => setData(data))
      .catch(() => toast.error(T.error))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  // URS-F187: Auto-scroll to current week on first load (once per year change).
  // The scrollable container is the <main> element in Layout.js. We delay via
  // setTimeout to ensure all 52 rows are committed to the DOM and the callback
  // ref for the current week row has been invoked.
  useEffect(() => {
    if (data && !loading && !scrolledRef.current) {
      const id = setTimeout(() => {
        const el = currentRowRef.current;
        const mainEl = document.querySelector('main');
        if (!el || !mainEl) return;
        const elRect = el.getBoundingClientRect();
        const mainRect = mainEl.getBoundingClientRect();
        // Position of the row relative to <main>, offset 120px so the
        // "ESTA SEMANA" badge sits near the top of the visible area.
        const offset = mainEl.scrollTop + (elRect.top - mainRect.top) - 120;
        mainEl.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
        scrolledRef.current = true;
      }, 250);
      return () => clearTimeout(id);
    }
  }, [data, loading]);

  const rows = useMemo(() => {
    if (!data) return [];
    const total = weeksInYear(year);
    const out = [];
    for (let w = 1; w <= total; w++) {
      const start = isoWeekStart(year, w);
      const end = new Date(start);
      end.setUTCDate(start.getUTCDate() + 6);
      const items = data.weeks?.[String(w)] || [];
      out.push({ week: w, start, end, items });
    }
    return out;
  }, [data, year]);

  const yearOptions = [];
  for (let y = currentYear - 1; y <= currentYear + 2; y++) yearOptions.push(y);

  const formatRange = (start, end) => {
    const sM = start.getUTCMonth();
    const eM = end.getUTCMonth();
    const sD = start.getUTCDate();
    const eD = end.getUTCDate();
    if (sM === eM) return `${sD}-${eD} ${MONTHS[sM].slice(0, 3).toLowerCase()}`;
    return `${sD} ${MONTHS[sM].slice(0, 3).toLowerCase()} - ${eD} ${MONTHS[eM].slice(0, 3).toLowerCase()}`;
  };

  return (
    <div className="space-y-4" data-testid="schedule-year-view">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <CalendarBlank size={22} weight="duotone" className="text-[#1f3e80]" />
          <h2 className="text-lg font-semibold text-stone-800">{T.title}</h2>
          <Select value={String(year)} onValueChange={v => setYear(Number(v))}>
            <SelectTrigger className="h-8 w-28 text-sm rounded-sm" data-testid="schedule-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {data && (
          <Badge variant="outline" className="font-mono text-xs" data-testid="schedule-total-badge">
            {data.total} {T.total}
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="text-center text-sm text-stone-400 py-12">{T.loading}</div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-stone-50 sticky top-0 z-10">
              <tr>
                <th className="border-b border-stone-200 text-left px-3 py-2 font-semibold text-stone-600 text-xs uppercase tracking-wide w-44">
                  {T.week}
                </th>
                <th className="border-b border-stone-200 text-left px-3 py-2 font-semibold text-stone-600 text-xs uppercase tracking-wide">
                  {T.schedule}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const isCurrent = year === todayIso.year && row.week === todayIso.week;
                return (
                  <tr
                    key={row.week}
                    ref={isCurrent ? (node) => { if (node) currentRowRef.current = node; } : undefined}
                    className={`border-b border-stone-100 last:border-b-0 ${isCurrent ? 'bg-[#1f3e80]/5' : 'hover:bg-stone-50/50'}`}
                    data-testid={`schedule-week-row-${row.week}`}
                  >
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-baseline gap-2">
                        <span className={`font-mono text-sm font-bold ${isCurrent ? 'text-[#1f3e80]' : 'text-stone-700'}`}>W{String(row.week).padStart(2, '0')}</span>
                        {isCurrent && (
                          <span className="text-[9px] font-semibold text-[#1f3e80] bg-[#1f3e80]/10 px-1.5 py-0.5 rounded-sm">{T.thisWeek}</span>
                        )}
                      </div>
                      <p className="text-[11px] text-stone-500 mt-0.5">{formatRange(row.start, row.end)}</p>
                    </td>
                    <td className="px-3 py-2">
                      {row.items.length === 0 ? (
                        <span className="text-xs text-stone-300 italic">{T.empty}</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {row.items.map((item, i) => (
                            <ScheduleChip key={i} item={item} lang={lang} navigate={navigate} T={T} />
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ScheduleChip({ item, lang, navigate, T }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border bg-white hover:bg-stone-50 transition-colors text-xs font-medium"
          style={{ borderColor: item.study_color, color: item.study_color }}
          data-testid={`schedule-chip-${item.study_id}-${item.visit_code}`}
          title={`${item.study_code} - ${item.visit_code}`}
        >
          <span className="font-mono">{item.study_code}</span>
          <span className="text-stone-400">·</span>
          <span className="font-semibold">{item.visit_code}</span>
          <span className="text-stone-500 font-mono">×{item.count}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="start">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm font-semibold text-stone-800 truncate" title={item.study_title}>{item.study_code}</p>
            <span className="text-[10px] font-mono text-stone-400">{item.study_id}</span>
          </div>
          {item.study_title && (
            <p className="text-xs text-stone-500 line-clamp-2">{item.study_title}</p>
          )}
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-semibold"
              style={{ borderColor: item.study_color, color: item.study_color }}>
              {item.visit_code}
            </span>
            <span className="text-[11px] text-stone-500">{item.count} {lang === 'es' ? 'huecos' : 'slots'}</span>
          </div>
          <div className="border-t border-stone-100 pt-2 max-h-40 overflow-y-auto space-y-1">
            {item.slots.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs text-stone-600 font-mono" data-testid={`schedule-slot-detail-${s.slot_id}`}>
                <span>{s.date}</span>
                <span className="text-stone-400">{s.start_time}-{s.end_time}</span>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs rounded-sm gap-1 border-[#1f3e80]/30 text-[#1f3e80] hover:bg-[#1f3e80]/5"
            onClick={() => navigate(`/studies/${item.study_id}`)}
            data-testid={`schedule-edit-in-study-${item.study_id}`}
          >
            {T.editInStudy}
            <CaretRight size={12} />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
